import React, { useState } from 'react';
import { StatusPill, Button, listPanelCls, listRowCls } from '../../components/ui';
import { IconFileText, IconArrowRight, IconChevronRight } from '../../components/icons';
import { useAuth } from '../../auth/AuthContext';
import { useNav } from '../../store/StoreContext';
import { getEstimateStatus } from '../../utils/';
import { optionsForMove, optionLabel, OPTION_TUCKED, fmtMoveDate, moveDatesSummary } from '../../lib/moves';

// Client file → Estimates tab. Shows the CURRENT MOVE's options (moves-
// architecture.md pick 3B, 2026-09-18): live / booked options as rows —
// the booked one prominent — and the tucked ones (declined, cancelled)
// folded under "Other quotes (n)". Actions: "Alternate pricing" = a new
// option on the same move (opens the New Estimate tab with this option's
// pricing copied), "+ New move" = the switcher's new move.
//
// Clicking an estimate navigates to the global Estimates page and auto-
// expands that row there. The parent client modal is closed via
// `onCloseParent` first so the operator lands on the full page view.
export default function ClientEstimatesTab({ estimates, clients, move = null, onCloseParent, onAlternatePricing, onNewMove }) {
    const { can } = useAuth();
    const canViewPricing = can('view_estimate_pricing');
    const { navigateToEstimate } = useNav();
    const [showTucked, setShowTucked] = useState(false);

    const handleOpen = (estimateId) => {
        // Close the client modal first, then jump to the estimate page.
        if (onCloseParent) onCloseParent();
        navigateToEstimate(estimateId);
    };

    // No move yet (legacy rows without move_id fall back to the whole list
    // so nothing disappears from the file).
    const options = move ? optionsForMove(estimates, move.id) : (estimates || []);
    const live = options.filter(e => !OPTION_TUCKED.has(e.status));
    const tucked = options.filter(e => OPTION_TUCKED.has(e.status));
    const booked = live.find(e => e.status === 'booked' || e.status === 'completed') || null;
    const sourceForAlternate = booked || live[0] || tucked[0] || null;

    const priceOf = (est) => {
        if (!canViewPricing || !(est.total_estimate > 0 || est.total_min > 0)) return null;
        return est.total_min && est.total_max && est.total_min !== est.total_max
            ? `$${Number(est.total_min).toLocaleString()} – $${Number(est.total_max).toLocaleString()}`
            : `$${Number(est.total_estimate || est.total_max || 0).toLocaleString()}`;
    };

    const row = (est, { prominent = false, dim = false } = {}) => {
        const status = getEstimateStatus(est.status);
        const price = priceOf(est);
        return (
            <div key={est.id}
                onClick={() => handleOpen(est.id)}
                className={`${listRowCls} ${prominent ? 'bg-emerald-50/70 border-l-4 border-l-emerald-500' : ''} ${dim ? 'opacity-70' : ''}`}>
                <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <span data-testid="estimate-status-label" data-estimate-id={est.id} className="inline-flex"><StatusPill label={status.label} color={status.color} bg={status.bg} /></span>
                            <span className="font-mono text-sm text-slate-700 font-bold">{optionLabel(est, options.length)}</span>
                            {prominent && <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">Booked option</span>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            {price && <span className={`text-lg font-bold ${prominent ? 'text-emerald-700' : 'text-green-600'}`}>{price}</span>}
                            <IconChevronRight size={16} className="text-slate-400" />
                        </div>
                    </div>
                    {!move && (est.origin_address || est.destination_address) && (
                        <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                            {est.origin_address && <span className="truncate max-w-[250px]">{est.origin_address}</span>}
                            {est.origin_address && est.destination_address && <IconArrowRight size={12} className="flex-shrink-0 text-slate-300"/>}
                            {est.destination_address && <span className="truncate max-w-[250px]">{est.destination_address}</span>}
                        </div>
                    )}
                    {!move && est.move_date && <div className="mt-1 text-xs text-slate-400">{fmtMoveDate(est.move_date, { year: true })}</div>}
                    <div className="mt-2 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Click to open on Estimates page &rarr;</div>
                </div>
            </div>
        );
    };

    // Actions row — same on the empty state and the list.
    const actions = (
        <div className="flex items-center gap-2 flex-wrap">
            {onAlternatePricing && move && (
                <Button variant="secondary" size="sm" onClick={() => onAlternatePricing(sourceForAlternate)}
                    title={sourceForAlternate ? `Price this move another way — starts from ${optionLabel(sourceForAlternate, options.length)}'s pricing, same dates and addresses` : 'Price this move — same dates and addresses'}>
                    {options.length ? 'Alternate pricing' : 'Price this move'}
                </Button>
            )}
            {onNewMove && <Button variant="tertiary" size="sm" onClick={onNewMove}>+ New move</Button>}
        </div>
    );

    if (options.length === 0) {
        return (
            <div className="text-center py-16 text-slate-400">
                <IconFileText size={48} className="mx-auto mb-3 opacity-40"/>
                <p className="text-lg font-medium">{move ? 'No estimates on this move yet' : 'No estimates yet'}</p>
                <p className="text-sm mt-1">{move ? `${moveDatesSummary(move)} — price it from the New Estimate tab` : 'Go to the New Estimate tab to create your first estimate for this client'}</p>
                <div className="mt-4 flex justify-center">{actions}</div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {move && (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{moveDatesSummary(move)}{options.length > 1 ? ` · ${options.length} options` : ''}</span>
                    {actions}
                </div>
            )}
            {/* Ruled list — one panel, hairline rows (lists.md §2). Booked
                option first and prominent; the rest of the live ones after. */}
            {live.length > 0 && (
                <div className={listPanelCls}>
                    {booked && row(booked, { prominent: true })}
                    {live.filter(e => e !== booked).map(est => row(est))}
                </div>
            )}
            {/* Other quotes — declined / cancelled options, folded. Never
                deleted: the dead quote often holds the deposit trail. */}
            {tucked.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <button type="button" onClick={() => setShowTucked(v => !v)} aria-expanded={showTucked}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-left text-[12px] font-bold text-slate-500 hover:bg-slate-50">
                        <span>Other quotes ({tucked.length})</span>
                        <span className={`text-[10px] transition-transform ${showTucked ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {showTucked && <div className="border-t border-slate-200">{tucked.map(est => row(est, { dim: true }))}</div>}
                </div>
            )}
            {!move && actions}
        </div>
    );
}
